import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Prop,
  h,
  Method,
  State,
  Watch,
} from "@stencil/core";
import { Instance, Options, createPopper } from "@popperjs/core";
import { IcTooltipPlacements } from "./ic-tooltip.types";
import {
  isSafari,
  onComponentRequiredPropUndefined,
  isSlotUsed,
  renderDynamicChildSlots,
} from "../../utils/helpers";
import { IcThemeMode } from "../../utils/types";

/**
 * @slot icon - Icon will be rendered to the left of the tooltip text.
 */

@Component({
  tag: "ic-tooltip",
  styleUrl: "ic-tooltip.css",
  shadow: true,
})
export class Tooltip {
  private arrow: HTMLDivElement;
  private delayedHideEvents = ["mouseleave"];
  private containerContentArea: HTMLDivElement | null;
  private instantHideEvents = ["focusout"];
  private mouseOverTool: boolean = false;
  private persistTooltip = false;
  private popperInstance: Instance;
  private showEvents = [
    !this.disableHover && "mouseenter",
    !this.disableHover && "focusin",
    !this.disableClick && "click",
  ];
  private toolTip: HTMLDivElement;
  private hostMutationObserver?: MutationObserver;

  @Element() el: HTMLIcTooltipElement;

  /**
   * If `true`, the tooltip will not be displayed on click, it will require hover or using the display method.
   */
  @Prop() disableClick?: boolean = false;

  @Watch("disableClick")
  watchDisableClickHandler(): void {
    if (this.disableClick) {
      this.hide();
    }
    this.updateTooltipEvents();
  }

  /**
   * If `true`, the tooltip will not be displayed on hover, it will require a click.
   */
  @Prop() disableHover?: boolean = false;

  @Watch("disableHover")
  watchDisableHoverHandler(): void {
    if (this.disableHover) {
      this.hide();
    }
    this.updateTooltipEvents();
  }

  /**
   * Setting to `true` can help in situations where tooltip content is clipped by a parent element.
   */
  @Prop() fixedPositioning: boolean = false;

  /**
   * The alt text to be used alongside a slotted icon.
   */
  @Prop() iconAltText?: string;

  /**
   * The number of lines to display before truncating the text.
   */
  @Prop() maxLines?: number;

  /**
   * The position of the tooltip in relation to the parent element.
   */
  @Prop() placement?: IcTooltipPlacements = "bottom";

  /**
   * @internal Sets the tooltip to aria-hidden, when used as part of components that are already announced.
   */
  @Prop() silent?: boolean = false;

  /**
   * The ID of the element the tooltip is describing - for when aria-labelledby or aria-describedby is used.
   */
  @Prop({ reflect: true }) target?: string;

  /**
   * Sets the tooltip to the dark or light theme colors. "inherit" will set the color based on the system settings or ic-theme component.
   */
  @Prop() theme?: IcThemeMode = "inherit";

  /**
   * The text to display on the tooltip.
   */
  @Prop() label!: string;
  @Watch("label")
  watchLabelHandler(): void {
    if (this.toolTip?.hasAttribute("data-show") && this.popperInstance) {
      this.popperInstance.update();
    }
  }

  @State() popperProps: Partial<Options> = {};

  /**
   * Emitted when the tooltip becomes visible.
   */
  @Event() icTooltipShow: EventEmitter<void>;

  /**
   * @internal This method allows props to be added to the PopperJS createPopper instance outside of tooltip
   * @param props object - createPopper props set externally
   */
  @Method()
  async setExternalPopperProps<T extends Partial<Options>>(props: T) {
    this.popperProps = props;
  }

  disconnectedCallback(): void {
    this.manageEventListeners("remove");
    if (this.popperInstance !== undefined) {
      this.popperInstance.destroy();
    }
  }

  componentDidLoad(): void {
    this.manageEventListeners("add");

    // Display tooltips correctly within drawer and dialog components
    let container: HTMLElement | null;

    if ((this.el.getRootNode() as ShadowRoot).host) {
      container = (this.el.getRootNode() as ShadowRoot).host.closest(
        "ic-dialog, ic-drawer"
      );
    } else {
      container = this.el.closest("ic-dialog, ic-drawer");
    }
    this.containerContentArea =
      container?.shadowRoot?.querySelector(".content-area") ?? null;
    this.hostMutationObserver = new MutationObserver((mutationList) =>
      renderDynamicChildSlots(mutationList, "icon", this)
    );
    this.hostMutationObserver.observe(this.el, {
      childList: true,
    });
    onComponentRequiredPropUndefined(
      [{ prop: this.label, propName: "label" }],
      "Tooltip"
    );
  }

  componentDidRender(): void {
    const typographyEl = this.el.shadowRoot?.querySelector(
      ".ic-tooltip-container > ic-typography"
    );
    this.maxLines &&
      this.maxLines > 0 &&
      typographyEl?.setAttribute(
        "style",
        `--truncation-max-lines: ${this.maxLines}`
      );

    if (this.iconAltText && !isSlotUsed(this.el, "icon")) {
      console.warn(
        `Prop 'iconAltText' is provided without an icon slotted in the 'icon' slot. Please provide an icon or remove the 'iconAltText' prop.`
      );
    }
  }

  /**
   * Method to programmatically show/hide the tooltip without needing to interact with an anchor element
   * @param show Whether to show or hide the tooltip
   * @param persistTooltip Whether the tooltip should stay on the screen when actions are performed that would previously dismiss the tooltip, such as on hover
   */
  @Method()
  async displayTooltip(show: boolean, persistTooltip?: boolean): Promise<void> {
    this.persistTooltip = !!persistTooltip;
    show ? this.show() : this.hide();
  }

  /**
   * @internal Method to return if tooltip is currently visible.
   */
  @Method()
  async isTooltipVisible(): Promise<boolean> {
    return Promise.resolve(this.toolTip.hasAttribute("data-show"));
  }

  private show = () => {
    if (this.label) {
      this.toolTip.setAttribute("data-show", "");

      if (this.containerContentArea) {
        this.el.classList.add("ic-tooltip-in-container");
      }

      this.popperInstance = createPopper(this.el, this.toolTip, {
        strategy: this.fixedPositioning ? "fixed" : "absolute",
        placement: this.placement,
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 10],
            },
          },
          {
            name: "arrow",
            options: {
              element: this.arrow,
            },
          },
          {
            name: "eventListeners",
            options: { scroll: false, resize: false },
          },
          ...(this.containerContentArea
            ? [
                {
                  name: "preventOverflow",
                  options: {
                    boundary: this.containerContentArea,
                    padding: 8,
                  },
                },
                {
                  name: "flip",
                  options: {
                    boundary: this.containerContentArea,
                  },
                },
              ]
            : []),
        ],
        ...this.popperProps,
      });
      this.icTooltipShow.emit();
    } else {
      console.warn(`Tooltip can't display without prop 'label' set`);
    }
  };

  private hide = () => {
    if (this.toolTip !== undefined) {
      this.toolTip.removeAttribute("data-show");
      this.persistTooltip = false;
    }
    if (this.popperInstance !== undefined) {
      this.popperInstance.destroy();
    }
  };

  private checkCloseTooltip = () => {
    setTimeout(() => {
      if (!this.mouseOverTool && !this.persistTooltip) {
        this.hide();
      }
    }, 100);
  };

  private mouseEnterTooltip = () => {
    this.mouseOverTool = true;
  };

  private mouseLeaveTooltip = () => {
    this.mouseOverTool = false;
    this.checkCloseTooltip();
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !this.persistTooltip) {
      this.hide();
    }
  };

  private manageEventListeners = (action: "add" | "remove") => {
    const method =
      action === "add" ? "addEventListener" : "removeEventListener";

    this.showEvents.forEach((event) => {
      if (event) {
        this.el[method](event, this.show);
        if (this.toolTip !== undefined) {
          this.toolTip[method](event, this.mouseEnterTooltip);
        }
      }
    });

    if (!this.persistTooltip) {
      this.instantHideEvents.forEach((event) => {
        this.el[method](event, this.hide);
      });
    }

    this.delayedHideEvents.forEach((event) => {
      this.el[method](event, this.checkCloseTooltip);
      if (this.toolTip !== undefined) {
        this.toolTip[method](event, this.mouseLeaveTooltip);
      }
    });

    document[method]("keydown", this.handleKeyDown as EventListener);
  };

  private updateTooltipEvents = () => {
    this.manageEventListeners("remove");
    this.showEvents = [
      !this.disableHover && "mouseenter",
      !this.disableHover && "focusin",
      !this.disableClick && "click",
    ];
    this.manageEventListeners("add");
  };

  render() {
    const { label, maxLines, silent, theme } = this;
    return (
      <Host
        class={{
          "ic-tooltip": true,
          [`ic-theme-${theme}`]: theme !== "inherit",
          "ic-tooltip-with-icon": isSlotUsed(this.el, "icon"),
        }}
        aria-label={isSafari && label} // accessible name needs to be in light dom to be announced by VoiceOver. Replace in v4 as aria-label on element with no role is not well-supported
      >
        <div
          ref={(el) => (this.toolTip = el as HTMLDivElement)}
          role="tooltip"
          class="ic-tooltip-container"
          aria-hidden={`${silent}`}
        >
          <div class="ic-tooltip-content">
            {isSlotUsed(this.el, "icon") && (
              <div
                class="ic-icon-container"
                aria-hidden={`${!this.iconAltText}`}
                aria-label={this.iconAltText}
                role="img"
              >
                <slot name="icon" aria-hidden="true"></slot>
              </div>
            )}
            <ic-typography maxLines={maxLines} variant="caption">
              {label}
            </ic-typography>
          </div>
          <div
            ref={(el) => (this.arrow = el as HTMLDivElement)}
            class="ic-tooltip-arrow"
          ></div>
        </div>
        <slot></slot>
      </Host>
    );
  }
}
